import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { FoundationsEnums } from '../../enum';
import { ChartPayload, TablePayload } from '../../Components/Reports/ReportFrame';
import { FoundationsConstants } from '../../FoundationsConstants';
import { VisualUnitApiDTO_VisualUnit } from '../../Interfaces/APIContracts/VisualUnitApiDTO_VisualUnit';
import {VisualUnitApiDTO_EditSaveVisualUnit} from '../../Interfaces/APIContracts/VisualUnitApiDTO_EditSaveVisualUnit';
import { RunVisualUnitWithId_VisualUnit } from '../../Interfaces/APIContracts/RunVisualUnitWithId_VisualUnit';
import { useExecuteChartMutation, useExecuteTableMutation } from './visualUnitAPISlice';

export interface IVuSettings {
    ApplySettings: boolean,
    ShowTableZeroValues: boolean,
    ShowTablePercentages: boolean,
    ShowGrandTotals: boolean,
    ShowGrandTotalRow: boolean,
    ShowGrandTotalCol: boolean,
    ShowNullOrEmpty: boolean,
    ShowRepeatedRows: boolean,
    ShowSubTotals: boolean
}

export interface ChartSettings {
    ApplySettings: boolean,
    ShowChartSummation: boolean,
    ChartSortOrder: number,
    ShowChartAnimation: boolean,
    ShowChartDataLabel: boolean,
    ShowChartZeroValue: boolean,
    ShowStacked: boolean,
    showPercentageTrend: boolean,
}

export interface ChartConfig {
    enable3dView:boolean,
    isFullScreen:boolean,
    isDrawing:boolean, 
    showLegend:boolean,
}

export interface ChartAxes {
    ApplySwitchValues: boolean,
    CategoryMetaDataId: number,
    CategoryMetaDataBinId: number,
    SeriesMetaDataId: number,
    SeriesMetaDataBinId: number,
    FilterMetaDataId: number,
    FilterMetaDataBinId: number
}

export interface chartFilterValue {
  metaDataId:number,
  filterValue:string
}

export interface IVUDataObj {
    additionalVUData: {},
    data_vuTempId: number,
    data_tempViewerType: string,
    data_viewerType: string | number,
    isHelpRequestDrillthru: string,
    data_vuDetails: any, //its a object with some properties like data_vuDetails.executeResultData
    TotalRows: number,
    engineType: string,
    Data_SortColumn: null,
    IsExpandedVisible: string,
    data_tableSettings: any,
    data_IsExpandedVisible: 'yes' | 'no' | '',
    data_totalRows: any,
    data_chartVariationType: string,
    data_drillThruDPList: any,
    data_tempTableSettings: any,
    data_isEnabledAutoNavigation: boolean,
    data_isSingleVU: boolean,
    data_pageNumber: number,
    data_pageSize: number,
    data_totalPages: number,
    data_chartAvailableTypes: string,
    data_sortColumnList: any[],
    data_sortColumn: string,
    data_sortOrder: any,
    data_SnapshotCount: number,
    mdscorderbyid?: any,
    data_chartSettings?: any,
    data_chartSwitch?:any
    data_tempChartSettings?:any
    data_chartData?:any,
    data_tempChartSwitch?: any,
    
}
export interface ReportsState {
  visualUnits:  { [vuId: number]: VisualUnitApiDTO_VisualUnit  | undefined };
  executeResultData: { [vuId: number]: RunVisualUnitWithId_VisualUnit | undefined };
    // tableSettings: { [vuId: number]: IVuSettings | undefined };
    // chartSettings:{  [vuId: number]: ChartSettings | undefined}
    chartConfigs: { [vuId: number]: ChartConfig };
    // chartAxes:{ [vuId: number]: ChartAxes | undefined}
    // chartFilters : { [vuId: number]: chartFilterValue | undefined};
    // viewerTypes: { [vuId: number]: number };
}

const initialState: ReportsState = {
    visualUnits: {},
    executeResultData: {},
    // tableSettings: {},
    // chartSettings: {},
    chartConfigs: {},
    // chartAxes: {},
    // chartFilters:{},
    // viewerTypes: {}
};

export interface IInitPayload {
    id: number;
    name: string;
    description: string;
    viewerType: number;
    preview: string;
    refreshInterval: string;
    privacyLock: string;
    measures: {
      id: number;
      displayOrder: number;
      axisSubType: number;
      aggregateFunction: string;
      businessParameterId: number;
      singleValueReportId: number;
      categoryName: string;
      compCategoryName: string[];
      factGroup: string[];
    }[];
    filterable: {
      id: number;
      displayOrder: number;
      filterValue: string;
      filterValueListId: number;
      filterDisplayText: string;
      filterOptions: string;
      InputFilterType: number;
    }[];
    dimensionRow: {
      id: number;
      displayOrder: number;
      metaDataBinId: number;
    }[];
    dimensionCol: any[]; // If the content of this array is unknown, you can update it later
    dashboardName: string;
    allowDrillThru: string;
    IsEnabledAutoNavigation: boolean;
    DrillthruPageSize: string;
    saveTableSettings: IVuSettings;
    refinementProfile: {
      measurables: any[]; // Specify the type if known
      filterables: any[]; // Specify the type if known
      stratifiables: any[]; // Specify the type if known
    };
    callerType: string;
    drillthrudp: {
      cellId: string;
      meId: number;
      vuDeId: number;
      dimDPs: {
        mdId: number;
        mdBId: number;
        val: string;
        drill: number;
      }[];
      vuId: number;
      sortColumns: any[]; // Specify the type if known
    }[];
    tempVuId: number;
    DrillthruSetMetadatId: number;
    DashBoardId: number;
  }

  export const initEditVuPayload: IInitPayload = {
    id: -1,
    name: "",
    description: "",
    viewerType: 1,
    preview: "N",
    refreshInterval: "0",
    privacyLock: "0",
    measures: [
      {
        id: 4002,
        displayOrder: 0,
        axisSubType: 0,
        aggregateFunction: "Total",
        businessParameterId: 0,
        singleValueReportId: -1,
        categoryName: "",
        compCategoryName: ["Cases"],
        factGroup: ["Patient"],
      },
    ],
    filterable: [
      {
        id: 4006,
        displayOrder: 0,
        filterValue: "Unknown",
        filterValueListId: 0,
        filterDisplayText: "Include any of [Unknown]",
        filterOptions: "INCLUDE_ANY",
        InputFilterType: 0,
      },
    ],
    dimensionRow: [
      {
        id: 4006,
        displayOrder: 0,
        metaDataBinId: 0,
      },
    ],
    dimensionCol: [],
    dashboardName: "",
    allowDrillThru: "Y",
    IsEnabledAutoNavigation: true,
    DrillthruPageSize: "10",
    saveTableSettings: {
      ApplySettings: false,
      ShowTableZeroValues: false,
      ShowTablePercentages: true,
      ShowGrandTotals: false,
      ShowGrandTotalCol: false,
      ShowGrandTotalRow: true,
      ShowNullOrEmpty: false,
      ShowRepeatedRows: false,
      ShowSubTotals: false,
    },
    refinementProfile: {
      measurables: [],
      filterables: [],
      stratifiables: [],
    },
    callerType: "Explore",
    drillthrudp: [
      {
        cellId: "41943_1_1",
        meId: 4002,
        vuDeId: 478085,
        dimDPs: [
          {
            mdId: 4006,
            mdBId: 0,
            val: "Unknown",
            drill: 1,
          },
        ],
        vuId: -1,
        sortColumns: [],
      },
    ],
    tempVuId: -1,
    DrillthruSetMetadatId: -1,
    DashBoardId: 3049,
  }

export interface IEditSaveVUPayload{
    initialSaveVUObj: { [vuId: number]: IInitPayload };
}
export const saveVUInitPayloadObj: IEditSaveVUPayload={
    initialSaveVUObj: {}
}
const EditSaveVUPayloadSlice = createSlice({
    name: 'editSaveVUPayload',
    initialState: saveVUInitPayloadObj,
    reducers: {
        setEditSetVUInitPayload: (state, action: PayloadAction<{ vuId: number; initialPayload: IInitPayload }>) => {
            const { vuId, initialPayload } = action.payload;
            state.initialSaveVUObj[vuId] = initialPayload; // Update settings by specific vuId
        },
    },
});
interface vudata {
    [vuid: number]: any; // Key is a number, value can be any type
  }
  
  // Create the slice
  const VudataSlice = createSlice({
    name: 'Vudata',
    initialState: {} as vudata, // Initial state is an empty object
    reducers: {
        SetVudata: (
        state,
        action: PayloadAction<{ vuId: number; data: any }>
      ) => {
        state[action.payload.vuId] = action.payload.data; 
      },
      deleteVudata: (state, action: PayloadAction<number>) => {
        const vuId = action.payload;
        delete state[vuId]; // Remove the key-value pair based on vuId
      },
    },
  });

export interface IActionsOpenState {
    isReportSettingOpen: boolean,
    isEditOpen: boolean,
    isActionOpen: boolean,
    isVuRefreshClicked: boolean
}
interface ReportActionsState {
    actions_State: { [vuId: number]: IActionsOpenState }
}
const reportActionsState: ReportActionsState = {
    actions_State: {}
}
const reportActionsStateSlice = createSlice({
    name: 'reportsactionstate',
    initialState: reportActionsState,
    reducers: {
        setReportActionsState: (state, action: PayloadAction<{ vuId: number; actionsOpenPayload: IActionsOpenState }>) => {
            const { vuId, actionsOpenPayload } = action.payload;
            state.actions_State[vuId] = actionsOpenPayload; //  settings by specific vuId
        },
    },
});

interface IReportVUInfo {
    reportVuInfo: { [vuId: number]: IVUDataObj }
}
const reportVuInfoState:IReportVUInfo  = {
    reportVuInfo : {}
}
const reportVuInfoSlice = createSlice({
    name: 'reportVuInfo',
    initialState: reportVuInfoState,
    reducers: {
        setReportVuInfoState: (state, action: PayloadAction<{ vuId: number; vuInfoPayload: IVUDataObj }>) => {
            const { vuId, vuInfoPayload } = action.payload;
            state.reportVuInfo[vuId] = vuInfoPayload; //  settings by specific vuId
        },
    },
});

export const { setReportActionsState } = reportActionsStateSlice.actions;
export const { setReportVuInfoState } = reportVuInfoSlice.actions;
export const getReportVUInfo= (state: RootState) => state.reportVUInfo.reportVuInfo;
export const getReportActionsState = (state: RootState) => state.openReportActions.actions_State
export const getEditSetVUInitPayload = (state: RootState) => state.EditSaveVUPayload.initialSaveVUObj;
export const { setEditSetVUInitPayload } = EditSaveVUPayloadSlice.actions;
export const editSaveInitialPayloadReducer = EditSaveVUPayloadSlice.reducer;

export interface Measure {
  id: number
  displayOrder: number
  axisSubType: number
  aggregateFunction: string
  businessParameterId: number
  singleValueReportId: number
  categoryName: string
  compCategoryName: string[]
  factGroup: string[]
}

export interface Filterable {
  id: number
  displayOrder: number
  filterValue: string
  filterValueListId: number | string
  filterDisplayText: string
  filterOptions: string
  InputFilterType: number
}
export interface IRunVuPayloadState {
  id: number,
  tempId: number,
  isAsync: boolean,
  viewerType: number,
  callerType: string,
  pageSize: number,
  pageNumber: number,
  grandTotal: boolean,
  sortColumn: any,
  sortOrder: any,
  forceRefresh: number,
  privacyLock: number,
  isAutoRefreshCall?: boolean,
  tableSettings?: IVuSettings | undefined
  BuildQuery?:boolean
}

export interface DimensionRow {
  id: number
  displayOrder: number
  metaDataBinId: number
}

export interface IVuParams {
  id: number,
  params: any
}

const VUParamsInfoSlice = createSlice({
  name: 'VUParamsInfo',
  initialState: [] as IVuParams[], // Initial state is an empty object
  reducers: {
    setVuParamsInfoStore: (
      state,
      action: PayloadAction<{ data: IVuParams[] }>
    ) => {
      const { data } = action.payload;
      return data;
    },
  },
});
const VUObjectInfoSlice = createSlice({
  name: 'VuObjInfo',
  initialState: [] as VisualUnitApiDTO_EditSaveVisualUnit[], // Initial state is an empty object
  reducers: {
    setVuObjectInfoStore: (
      state,
      action: PayloadAction<{ data: VisualUnitApiDTO_EditSaveVisualUnit[] }>
    ) => {
      const { data } = action.payload;
      return data
    },
  },
});

const runVuDataPayloadSlice = createSlice({
  name: 'runVuDataPayload',
  initialState: {} as ChartPayload | TablePayload,
  reducers: {
    setRunVuDataPayload: (
      state,
      action: PayloadAction<ChartPayload | TablePayload>
    ) => {
      return {...action.payload};
    }
  },
});

export const {SetVudata, deleteVudata} = VudataSlice.actions;
export const vudataReducer = VudataSlice.reducer;
export const reportVUInfoReducer = reportVuInfoSlice.reducer;
export const openReportActionsReduecer = reportActionsStateSlice.reducer;
export const { setVuParamsInfoStore } = VUParamsInfoSlice.actions;
export const { setVuObjectInfoStore } = VUObjectInfoSlice.actions;
export const vuObjInfoReducer = VUObjectInfoSlice.reducer;
export const vuParamsInfoReducer = VUParamsInfoSlice.reducer
export const getVuObjInfoState = (state: RootState) => state.VuObjectInfo
export const getVUParamsInfoState = (state: RootState) => state.VuParamsInfo
export const RunVUDataPayloadReducer = runVuDataPayloadSlice.reducer;
